import { useCallback } from "react";

import type { MutationFunction } from "@tanstack/react-query";
import type { AxiosError, AxiosResponse } from "axios";

import { useApi } from "../api/api";
import type { useToastNotificationsProps } from "./useToastNotifications";
import { useToastNotifications } from "./useToastNotifications";
import { filterObjectKeys } from "../lib/utils";

interface useMutateProps extends useToastNotificationsProps {
  method: "post" | "put" | "patch" | "delete";
  url: string;
  apiVersion?: "v1" | "v2";
}

export function useMutate<TRequest>({
  method,
  url,
  toast,
  refetchQueries,
  apiVersion = "v1",
}: useMutateProps): {
  mutationFn: MutationFunction<AxiosResponse, TRequest>;
  onSuccess: () => void;
  onError: (error: AxiosError) => void;
} {
  const { api } = useApi({ version: apiVersion });

  const { onSuccess, onError } = useToastNotifications({
    toast,
    refetchQueries,
  });

  const mutationFn: MutationFunction<AxiosResponse, TRequest> = useCallback(
    async (data) => {
      return api({
        method,
        url,
        data:
          typeof data === "object" && data !== null
            ? filterObjectKeys(data)
            : data,
      });
    },
    [api, method, url],
  );

  return { mutationFn, onSuccess, onError };
}
